import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../config/api';
import MainHeader from '../../components/MainHeader';
import Footer from '../../components/Footer';
import './MainScreens.css';
import './FavoritesScreen.css';

export default function FavoritesScreen() {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search state
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingIds, setAddingIds] = useState(new Set());
  const searchTimeout = useRef(null);

  const fetchFavorites = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/favorites');
      setFavorites(data.favorites || []);
    } catch (err) {
      console.error('Failed to load favorites:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  // Get user location for search
  function getUserLocation() {
    return new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve(null),
        { timeout: 5000 }
      );
    });
  }

  async function searchRestaurants(query) {
    if (query.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const loc = await getUserLocation();
      const { data } = await api.post('/restaurants/search', {
        query,
        latitude: loc?.lat,
        longitude: loc?.lng,
      });
      setSearchResults(data.restaurants || data.results || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }

  function onSearchChange(e) {
    const text = e.target.value;
    setSearchQuery(text);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchRestaurants(text), 400);
  }

  async function addToFavorites(restaurant) {
    setAddingIds(prev => new Set(prev).add(restaurant.id));
    try {
      await api.post('/favorites', {
        placeId: restaurant.id,
        name: restaurant.name,
        cuisine: restaurant.cuisine,
        address: restaurant.address,
        image: restaurant.image,
        rating: restaurant.rating,
        priceLevel: restaurant.priceLevel,
        coordinates: restaurant.coordinates,
      });
      fetchFavorites();
    } catch {
      // ignore
    } finally {
      setAddingIds(prev => {
        const next = new Set(prev);
        next.delete(restaurant.id);
        return next;
      });
    }
  }

  async function removeFavorite(placeId, name) {
    if (!window.confirm(`Remove ${name} from your favorites?`)) return;
    setFavorites(prev => prev.filter(f => f.id !== placeId));
    try {
      await api.delete(`/favorites/${placeId}`);
    } catch {
      fetchFavorites();
    }
  }

  function openMaps(fav) {
    if (fav.coordinates?.latitude && fav.coordinates?.longitude) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${fav.coordinates.latitude},${fav.coordinates.longitude}`, '_blank');
    } else if (fav.address) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fav.name + ' ' + fav.address)}`, '_blank');
    }
  }

  function orderOnDoorDash(name) {
    window.open(`https://www.doordash.com/search/store/${encodeURIComponent(name)}/`, '_blank');
  }

  const favoritedIds = new Set(favorites.map(f => f.id));

  if (loading) {
    return (
      <div className="main-screen">
        <MainHeader title="My Favorites" showBack={true} backPath="/home" />
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading favorites...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-screen">
      <MainHeader 
        title="My Favorites" 
        showBack={true} 
        backPath="/home"
        rightButton={
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button className="fav-search-toggle" onClick={() => setShowSearch(true)}>
              🔍 Add
            </button>
            <span className="fav-count">{favorites.length}</span>
          </div>
        }
      />

      <main className="screen-main">
        <div className="favorites-container">

          {/* Swipe Through Favorites Banner */}
          {favorites.length > 2 && (
            <button className="fav-swipe-banner" onClick={() => navigate('/preferences', { state: { source: 'favorites' } })}>
              <span className="fav-swipe-emoji">👆</span>
              <div className="fav-swipe-text">
                <div className="fav-swipe-title">Swipe Through Favorites</div>
                <div className="fav-swipe-sub">Can't decide? Let us pick from your favorites!</div>
              </div>
              <span className="fav-swipe-arrow">→</span>
            </button>
          )}

          {favorites.length === 0 ? (
            <div className="empty-state">
              <div className="empty-emoji">🤍</div>
              <h2 className="empty-title">No favorites yet</h2>
              <p className="empty-text">
                Tap 🔍 Add above to search for restaurants, or heart any restaurant in your session results!
              </p>
              <button className="btn btn-primary" onClick={() => setShowSearch(true)}>
                🔍 Search Restaurants
              </button>
            </div>
          ) : (
            <div className="favorites-list">
              {favorites.map(fav => (
                <div key={fav.id} className="favorite-card">
                  {fav.image ? (
                    <div className="fav-image" style={{ backgroundImage: `url(${fav.image})` }}></div>
                  ) : (
                    <div className="fav-image fav-image-placeholder">
                      <span>🍽️</span>
                    </div>
                  )}
                  <div className="fav-content">
                    <div className="fav-header">
                      <h3 className="fav-name">{fav.name}</h3>
                      <button
                        className="fav-heart-btn"
                        onClick={() => removeFavorite(fav.id, fav.name)}
                        title="Remove from favorites"
                      >
                        ❤️
                      </button>
                    </div>
                    <p className="fav-cuisine">{fav.cuisine}</p>
                    <div className="fav-meta">
                      <span>{'$'.repeat(fav.priceLevel || 1)}</span>
                      {fav.rating > 0 && <span>⭐ {fav.rating.toFixed(1)}</span>}
                    </div>
                    {fav.address && (
                      <p className="fav-address" onClick={() => openMaps(fav)}>
                        📍 {fav.address}
                      </p>
                    )}
                    <div className="fav-actions">
                      <button className="fav-action-btn" onClick={() => openMaps(fav)}>
                        📍 Directions
                      </button>
                      <button className="fav-action-btn order" onClick={() => orderOnDoorDash(fav.name)}>
                        🚗 Order
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Search Modal */}
      {showSearch && (
        <div className="fav-search-overlay">
          <div className="fav-search-modal">
            <div className="fav-search-header">
              <h2 className="fav-search-title">Search Restaurants</h2>
              <button className="fav-search-close" onClick={() => { setShowSearch(false); setSearchQuery(''); setSearchResults([]); }}>✕</button>
            </div>

            <div className="fav-search-input-wrap">
              <span className="fav-search-icon">🔍</span>
              <input
                className="fav-search-input"
                type="text"
                placeholder="Search by name or cuisine..."
                value={searchQuery}
                onChange={onSearchChange}
                autoFocus
              />
              {searchQuery && (
                <button className="fav-search-clear" onClick={() => { setSearchQuery(''); setSearchResults([]); }}>✕</button>
              )}
            </div>

            <div className="fav-search-results">
              {searching ? (
                <div className="fav-search-state">
                  <div className="spinner"></div>
                  <p>Searching nearby...</p>
                </div>
              ) : searchResults.length > 0 ? (
                searchResults.map(r => {
                  const isFaved = favoritedIds.has(r.id);
                  const isAdding = addingIds.has(r.id);
                  return (
                    <div key={r.id} className="fav-search-result">
                      {r.image ? (
                        <img src={r.image} alt={r.name} className="fav-sr-image" />
                      ) : (
                        <div className="fav-sr-image fav-sr-placeholder">🍽️</div>
                      )}
                      <div className="fav-sr-info">
                        <div className="fav-sr-name">{r.name}</div>
                        <div className="fav-sr-sub">
                          {r.cuisine} {r.rating > 0 ? `· ⭐ ${r.rating.toFixed(1)}` : ''}
                        </div>
                        {r.address && <div className="fav-sr-addr">📍 {r.address}</div>}
                      </div>
                      <button
                        className={`fav-sr-add ${isFaved ? 'fav-sr-added' : ''}`}
                        onClick={() => !isFaved && !isAdding && addToFavorites(r)}
                        disabled={isFaved || isAdding}
                      >
                        {isAdding ? '...' : isFaved ? '❤️' : '🤍'}
                      </button>
                    </div>
                  );
                })
              ) : searchQuery.length >= 2 ? (
                <div className="fav-search-state">
                  <span style={{ fontSize: 48 }}>🍽️</span>
                  <p>No restaurants found for "{searchQuery}"</p>
                </div>
              ) : (
                <div className="fav-search-state">
                  <span style={{ fontSize: 48 }}>🔍</span>
                  <p>Type at least 2 characters to search</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
